import { ref, reactive, nextTick } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { request } from '@/util/globalContext';
import { listCommon } from '@/util/vueutil/commonList.js';

export default function useVerifyRecordList(listRef) {
  // 超级管理员判断：roleId === '001'
  const isAdmin = sessionStorage.getItem('currentRole') === '001';

  const searchForm = reactive({
    itemName: '',
    settleStatus: '',
    verifyTimeRange: [],
    verifyUsername: '',
    verifyUserId: ''
  });

  const selectedRows = ref([]);

  const {
    handleReset
  } = listCommon(searchForm);

  // 多选选中项变更
  const handleSelectionChange = (val) => {
    selectedRows.value = val || [];
  };

  // 提取查询参数（带时间范围拆分）
  const buildSearchParams = () => {
    const params = {
      itemName: searchForm.itemName,
      settleStatus: searchForm.settleStatus
    };
    // 超管才传核验员筛选条件
    if (isAdmin) {
      params.verifyUsername = searchForm.verifyUsername;
      params.verifyUserId = searchForm.verifyUserId;
    }
    if (Array.isArray(searchForm.verifyTimeRange) && searchForm.verifyTimeRange.length === 2) {
      const [start, end] = searchForm.verifyTimeRange;
      params.verifyStartTime = start ? start + ' 00:00:00' : '';
      params.verifyEndTime = end ? end + ' 23:59:59' : '';
    }
    return params;
  };

  // 列表上方显示的积分和
  const sumInfo = reactive({
    visible: false,
    label: '',
    count: 0,
    totalPoints: 0
  });

  const handleLoadList = async (paginationParams) => {
    if (!listRef.value) return;
    if (!listRef.value.isInited) {
      await nextTick();
      if (!listRef.value?.isInited) {
        ElMessage.warning('列表组件初始化中，请稍后重试');
        return;
      }
    }

    listRef.value?.setLoading(true);
    try {
      const params = {
        ...paginationParams,
        ...buildSearchParams()
      };
      const res = await request.post('/exchange/myVerifyRecords', params);

      if (res.code === 200) {
        listRef.value.updateListData(res.data.records || []);
        listRef.value.updatePagination({
          pageNum: paginationParams.pageNum,
          pageSize: paginationParams.pageSize,
          total: res.data.total || 0
        });
      } else {
        ElMessage.error('列表加载失败：' + (res.msg || '接口异常'));
        listRef.value.updateListData([]);
        listRef.value.updatePagination({ ...paginationParams, total: 0 });
      }
    } catch (error) {
      ElMessage.error('列表加载失败：网络异常');
      listRef.value.updateListData([]);
      listRef.value.updatePagination({ ...paginationParams, total: 0 });
    } finally {
      listRef.value?.setLoading(false);
    }
  };

  // 结算选中的记录
  const handleSettle = async () => {
    if (!selectedRows.value.length) {
      ElMessage.warning('请先选择需要结算的记录');
      return;
    }
    const ids = selectedRows.value.map(r => r.id);
    try {
      await ElMessageBox.confirm(
        `确认结算选中的 ${ids.length} 条核验记录吗？（已结算的将自动跳过）`,
        '批量结算',
        { confirmButtonText: '确认', cancelButtonText: '取消', type: 'warning', center: true }
      );
    } catch {
      return;
    }

    try {
      const res = await request.post('/exchange/settleVerifyRecords', { ids });
      if (res.code === 200) {
        ElMessage.success(res.msg || '结算成功');
        sumInfo.visible = false;
        const pagination = listRef.value.getPagination();
        handleLoadList(pagination);
      } else {
        ElMessage.error(res.msg || '结算失败');
      }
    } catch (error) {
      ElMessage.error('结算失败：网络异常');
    }
  };

  // 全部结算（按当前查询条件）
  const handleSettleAll = async () => {
    try {
      await ElMessageBox.confirm(
        '确认按当前查询条件结算所有未结算的核验记录吗？',
        '全部结算',
        { confirmButtonText: '确认', cancelButtonText: '取消', type: 'warning', center: true }
      );
    } catch {
      return;
    }

    try {
      const res = await request.post('/exchange/settleAllVerifyRecords', buildSearchParams());
      if (res.code === 200) {
        ElMessage.success(res.msg || '结算成功');
        sumInfo.visible = false;
        const pagination = listRef.value.getPagination();
        handleLoadList(pagination);
      } else {
        ElMessage.error(res.msg || '结算失败');
      }
    } catch (error) {
      ElMessage.error('结算失败：网络异常');
    }
  };

  // 计算积分和
  const handleCalcPoints = async () => {
    // 选中了某些数据：直接前端计算选中的积分和（不论是否已结算）
    if (selectedRows.value.length) {
      const selected = selectedRows.value;
      const totalPoints = selected.reduce((sum, r) => sum + (Number(r.requiredPoints) || 0), 0);
      sumInfo.visible = true;
      sumInfo.label = '选中合计';
      sumInfo.count = selected.length;
      sumInfo.totalPoints = totalPoints;
      return;
    }

    // 没选中：调接口查所有未结算的积分和
    try {
      const res = await request.post('/exchange/sumUnsettledPoints', buildSearchParams());
      if (res.code === 200) {
        sumInfo.visible = true;
        sumInfo.label = '未结算合计';
        sumInfo.count = res.data.count || 0;
        sumInfo.totalPoints = res.data.totalPoints || 0;
      } else {
        ElMessage.error(res.msg || '查询失败');
      }
    } catch (error) {
      ElMessage.error('查询失败：网络异常');
    }
  };

  return {
    isAdmin,
    searchForm,
    selectedRows,
    sumInfo,
    handleReset,
    handleSelectionChange,
    handleLoadList,
    handleSettle,
    handleSettleAll,
    handleCalcPoints
  };
}
